import { createFeatureSelector, createSelector } from '@ngrx/store';
import { COMMITMENTS_INTERACTION_BAR_TYPE } from '../../framework/constants/interaction-bar.constants';
import { templateSelectors } from '../templates/templates.selectors';
import { IBudgetTagTemplate } from '../templates/templates.types';
import { commitmentsFeatureKey } from './commitments.reducer';
import {
  COMMITMENT_APPROVAL_STATUS,
  CommitmentsState,
  ISidebarCommitment,
  ISidebarCommitmentItem,
} from './commitments.types';
import {
  ContractSummaryCommitmentItem,
  ContractSummaryCommitmentItemCost,
  ContractSummaryCommitmentItemDto,
  InvoiceCost,
} from './contract-summary.commitments.types';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { DeepCopyService } from '@app/services/deep-copy.service';

const getCommitmentsState = createFeatureSelector<CommitmentsState>(commitmentsFeatureKey);

const selectCommitmentsSummary = createSelector(
  getCommitmentsState,
  (state) => state.allCommitmentsSummary,
);
const getTemporaryContractors = createSelector(
  getCommitmentsState,
  (state) => state.temporaryContractors,
);

const selectAllContractors = createSelector(
  viewProjectSelectors.selectInvitedServiceProviders,
  getTemporaryContractors,
  (invitedContractors, temporaryContractors) => {
    return {
      invitedContractors: invitedContractors ?? [],
      temporaryContractors: temporaryContractors ?? [],
    };
  },
);

const selectAllContractorsAsArray = createSelector(
  viewProjectSelectors.selectInvitedServiceProviders,
  getTemporaryContractors,
  (invitedContractors, temporaryContractors) => {
    return [...(invitedContractors ?? []), ...(temporaryContractors ?? [])];
  },
);

const currentSidebarPage = createSelector(getCommitmentsState, (state) => state.sidebarPage);

const getDirectCostsSummary = createSelector(
  getCommitmentsState,
  (state) => state.directCostsSummary ?? { direct_costs: [] },
);

const getSelectedCommitment = createSelector(
  getCommitmentsState,
  (state) => state.selectedCommitment,
);

const getSelectedContract = createSelector(getCommitmentsState, (state) => {
  const selectedContract = DeepCopyService.deepCopy(state.selectedContract);
  if (!selectedContract) {
    return null;
  }
  selectedContract.change_orders.forEach((changeOrder) => {
    changeOrder.totalDuration = changeOrder.commitment_items.reduce(
      (acc, cur) => acc + cur.duration,
      0,
    );
  });

  return selectedContract;
});

const selectSelectedPrimeContract = createSelector(getCommitmentsState, (state) => {
  return state.selectedPrimeContract;
});

const selectContractSpendEntries = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarContract?.commitment_items
    .map((item) => {
      const filteredCosts = item.costs
        .filter((cost) => !cost?.deleted)
        .map((cost) => {
          return {
            ...cost,
            is_disabled:
              (state.sideBarContract.approval_status === COMMITMENT_APPROVAL_STATUS.APPROVED &&
                !!state?.sideBarContract?.id) ||
              !!cost?.is_disabled,
          };
        });
      return { ...item, costs: filteredCosts };
    })
    .filter((item) => !item?.deleted);
});

const getChangeOrderEntries = createSelector(getCommitmentsState, (state) => {
  const contract = state?.sidebarSelectedContractSummary?.contract;
  return state?.sideBarChangeOrder?.commitment_items
    .map((commitmentItem) => {
      const filteredCosts = commitmentItem.costs
        .filter((cost) => !cost?.deleted)
        .map((cost) => {
          return {
            ...cost,
            is_disabled:
              (state.sideBarChangeOrder.approval_status === COMMITMENT_APPROVAL_STATUS.APPROVED &&
                !!state?.sideBarChangeOrder?.id) ||
              !!cost?.is_disabled,
          };
        });
      if (!contract) {
        return { ...commitmentItem, costs: filteredCosts };
      }

      const foundItem = contract.commitment_items.find(
        (ci) => ci.item?.id === commitmentItem.item?.id,
      );

      return {
        ...commitmentItem,
        item: {
          ...commitmentItem.item,
          contractApproved: !!foundItem,
        },
        costs: filteredCosts,
      };
    })
    .filter((item) => !item?.deleted);
});

const getDirectCostSpendEntries = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarDirectCost?.commitment_items
    .map((item) => {
      const filteredCosts = item.costs.filter((cost) => !cost?.deleted);
      return { ...item, costs: filteredCosts };
    })
    .filter((item) => !item?.deleted);
});

const selectSideBarContractSOVs = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarContract?.prime_contract_sovs ?? [];
});

const selectSideBarContract = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarContract;
});

const getSidebarInvoice = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarInvoice;
});

const getSidebarDirectCost = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarDirectCost;
});

const getSideBarChangeOrder = createSelector(getCommitmentsState, (state) => {
  return state?.sideBarChangeOrder;
});

const selectSidebarCommitment = (commitmentType: COMMITMENTS_INTERACTION_BAR_TYPE) =>
  createSelector(getCommitmentsState, (state): ISidebarCommitment => {
    switch (commitmentType) {
      case COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT:
      case COMMITMENTS_INTERACTION_BAR_TYPE.ADD_CONTRACT: {
        return state?.sideBarContract;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER: {
        return state?.sideBarChangeOrder;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES: {
        return state?.sideBarInvoice;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.DIRECT_COST: {
        return state?.sideBarDirectCost;
      }
    }
  });

const getInvoiceCosts = createSelector(getCommitmentsState, (state) =>
  state.sideBarInvoice.costs.filter((cost) => !cost?.deleted),
);

const reduceCost = (acc, cost) => {
  return Number(acc ?? 0) + Number(cost?.value ?? 0);
};

const reduceEntry = (acc, entry) => {
  return acc + entry.costs.reduce(reduceCost, 0);
};

const totalContractCostDescription = createSelector(selectContractSpendEntries, (entries) => {
  return entries.reduce(reduceEntry, 0);
});

const totalChangeOrderCostDescription = createSelector(getChangeOrderEntries, (entries) => {
  return entries.reduce(reduceEntry, 0);
});

const totalInvoiceCostDescription = createSelector(getInvoiceCosts, (costs) => {
  return costs.reduce(reduceCost, 0);
});

const totalDirectCostsDescriptionTotals = createSelector(getDirectCostSpendEntries, (entries) => {
  return entries.reduce(reduceEntry, 0);
});

const getProjectData = createSelector(getCommitmentsState, (state) => state.projectData);

const selectContractsFromSummary = createSelector(
  getCommitmentsState,
  (state) => state.allCommitmentsSummary?.contracts ?? [],
);

const selectApprovedContractsFromSummary = createSelector(
  selectContractsFromSummary,
  (contracts) =>
    contracts.filter(
      (contract) => contract.approval_status === COMMITMENT_APPROVAL_STATUS.APPROVED,
    ) ?? [],
);

// project has at least one approved contract
const selectProjectHasApprovedContract = createSelector(
  selectApprovedContractsFromSummary,
  (approvedContracts) => approvedContracts.length > 0,
);

const getSidebarSelectedContractSummary = createSelector(getCommitmentsState, (state) => {
  return state.sidebarSelectedContractSummary;
});

/**
 * Role of this selector is to filter the costs by the budget tag id and line item id from the currently selected contract
 * @param index
 * @param lineId
 * @param budgetTagId
 */

const getSelectableLineItemsForChangeOrder = (index: number) =>
  createSelector(getCommitmentsState, (state) => {
    const changeOrderCommitmentItem: ISidebarCommitmentItem =
      state.sideBarChangeOrder.commitment_items[index];
    // console.log(index, state.sideBarChangeOrder, changeOrderCommitmentItem);
    const budgetTagId = changeOrderCommitmentItem?.budget_tag?.id;
    const lineId = changeOrderCommitmentItem?.item?.id;

    if (!lineId || !state.sidebarSelectedContractSummary) {
      return [];
    }

    const contract = state.sidebarSelectedContractSummary.contract;

    const commitmentItems = contract.commitment_items.filter((commitmentItem) => {
      if (budgetTagId) {
        return (
          commitmentItem?.item?.id === lineId && commitmentItem?.budget_tag?.id === budgetTagId
        );
      }
      return commitmentItem?.item?.id === lineId && !commitmentItem?.budget_tag?.id;
    });

    const extendedCommitmentItems: ContractSummaryCommitmentItem[] = commitmentItems.map(
      (commitmentItem) => {
        const costs: ContractSummaryCommitmentItemCost[] = commitmentItem.costs
          .map((contractCost) => {
            const is_disabled = !!changeOrderCommitmentItem.costs.find(
              (cost) =>
                (cost.parent_id && cost.parent_id === contractCost.id) ||
                cost.id === contractCost.id,
            );
            return {
              ...contractCost,
              is_disabled,
            };
          })
          .filter(
            (contractCost) =>
              !changeOrderCommitmentItem.costs.find(
                (changeOrderCost) => changeOrderCost.id === contractCost.id,
              ),
          );

        return {
          ...commitmentItem,
          costs,
        };
      },
    );

    return extendedCommitmentItems;
  });

const getSelectableInvoiceCosts = createSelector(getCommitmentsState, (state) => {
  const budgetTagId = state.sideBarInvoice?.budget_tag?.id;

  if (!state.sidebarSelectedContractSummary) {
    return [];
  }

  const contract = state.sidebarSelectedContractSummary.contract;

  const commitmentItems: ContractSummaryCommitmentItemDto[] = contract.commitment_items.filter(
    (commitmentItem) => {
      if (budgetTagId) {
        return commitmentItem?.budget_tag?.id === budgetTagId;
      }
      return true;
    },
  );

  return commitmentItems.flatMap((item) => {
    return item.costs.flatMap((cost) => {
      const isDisabled = !!state.sideBarInvoice.costs.find(
        (selectedCost) => selectedCost.parent_id && selectedCost.parent_id === cost.id,
      );
      return {
        ...cost,
        value: cost.value,
        is_disabled: isDisabled,
        item: item.item,
        budget_tag: item.budget_tag,
      } as InvoiceCost;
    });
  });
});

const selectIsSidebarLoading = createSelector(getCommitmentsState, (state) => {
  return state.isLoadingCommitment || state.temporaryContractorsLoading;
});
const isSelectedCommitmentsSummaryLoading = createSelector(
  getCommitmentsState,
  (state) => state.isSelectedCommitmentViewSummaryLoading,
);

const getSidebarChanged = createSelector(getCommitmentsState, (state) => state.sideBarChanged);

const getHasCommitmentDependency = (commitmentType: COMMITMENTS_INTERACTION_BAR_TYPE) =>
  createSelector(getCommitmentsState, (state) => {
    switch (commitmentType) {
      case COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT:
      case COMMITMENTS_INTERACTION_BAR_TYPE.ADD_CONTRACT: {
        return !!state?.sideBarContract?.has_commitments;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.CHANGE_ORDER: {
        return !!state?.sideBarChangeOrder?.has_commitments;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.INVOICES: {
        return !!state?.sideBarInvoice?.has_commitments;
      }
      case COMMITMENTS_INTERACTION_BAR_TYPE.DIRECT_COST: {
        return !!state?.sideBarDirectCost?.has_commitments;
      }
      default: {
        return false;
      }
    }
  });

const canAddMoreInvoiceCost = createSelector(getCommitmentsState, (state) => {
  const totalSelectableCosts =
    state.sidebarSelectedContractSummary.contract.commitment_items.reduce(
      (acc, item) => (acc += item.costs.length),
      0,
    );
  return state.sideBarInvoice.costs.length < totalSelectableCosts;
});

const reduceTemplateList = (acc: number, template: IBudgetTagTemplate) => {
  return acc + (template.tags.length ?? 0);
};

const canAddContractSpendEntries = createSelector(
  selectSideBarContract,
  viewProjectSelectors.selectLineItemsSummary,
  selectContractSpendEntries,
  templateSelectors.getBudgetTagTemplates,
  (sideBarContract, lineItems, entries: ISidebarCommitmentItem[], budgeTemplates) => {
    const tags = budgeTemplates.reduce(reduceTemplateList, 0);
    // note: can add if entries are less than line items * (tags + 1) combo, + 1 is empty tag
    // AND contract is not being edited(if has id then it's edit mode) with approved status
    return (
      entries.length < lineItems.length * (tags + 1) &&
      !(
        sideBarContract.approval_status === COMMITMENT_APPROVAL_STATUS.APPROVED &&
        !!sideBarContract.id
      )
    );
  },
);

const canAddChangeOrderSpendEntries = createSelector(
  getSideBarChangeOrder,
  viewProjectSelectors.selectLineItemsSummary,
  getChangeOrderEntries,
  templateSelectors.getBudgetTagTemplates,
  (sidebarChangeOrder, lineItems, entries: ISidebarCommitmentItem[], budgeTemplates) => {
    const tags = budgeTemplates.reduce(reduceTemplateList, 0);
    // note: can add if entries are less than line items * (tags + 1) combo, + 1 is empty tag
    // AND CO is not being edited(if has id then it's edit mode) with approved status
    return (
      entries.length < lineItems.length * (tags + 1) &&
      !(
        sidebarChangeOrder.approval_status === COMMITMENT_APPROVAL_STATUS.APPROVED &&
        !!sidebarChangeOrder.id
      )
    );
  },
);

const canAddDirectCostSpendEntries = createSelector(
  viewProjectSelectors.selectLineItemsSummary,
  getDirectCostSpendEntries,
  templateSelectors.getBudgetTagTemplates,
  (lineItems, entries: ISidebarCommitmentItem[], budgeTemplates) => {
    const tags = budgeTemplates.reduce(reduceTemplateList, 0);
    return entries.length < lineItems.length * (tags + 1);
  },
);

const canAddInvoiceCosts = createSelector(
  getSidebarInvoice,
  getSidebarSelectedContractSummary,
  (invoice, contractSummary) => {
    return (
      invoice?.costs?.length <
      contractSummary?.contract?.commitment_items?.reduce(
        (acc, item) => (acc += item?.costs?.length ?? 0),
        0,
      )
    );
  },
);

export const commitmentsSelectors = {
  getCommitmentsState,
  selectCommitmentsSummary,
  getTemporaryContractors,
  getDirectCostsSummary,
  getSelectedCommitment,
  getSelectedContract,
  selectSelectedPrimeContract,
  selectAllContractors,
  selectAllContractorsAsArray,
  currentSidebarPage,
  selectContractSpendEntries,
  selectSideBarContractSOVs,
  selectSideBarContract,
  totalContractCostDescription,
  totalChangeOrderCostDescription,
  getProjectData,
  selectContractsFromSummary,
  selectApprovedContractsFromSummary,
  selectProjectHasApprovedContract,
  getSideBarChangeOrder,
  getChangeOrderEntries,
  getSelectableLineItemsForChangeOrder,
  getSidebarSelectedContractSummary,
  getSidebarInvoice,
  totalInvoiceCostDescription,
  totalDirectCostsDescriptionTotals,
  getInvoiceCosts,
  selectSidebarCommitment,
  selectIsSidebarLoading,
  isSelectedCommitmentsSummaryLoading,
  getSidebarDirectCost,
  getDirectCostSpendEntries,
  getSelectableInvoiceCosts,
  getSidebarChanged,
  getHasCommitmentDependency,
  canAddMoreInvoiceCost,
  canAddInvoiceCosts,
};

export const commitmentCombinedSelectors = {
  canAddContractSpendEntries,
  canAddChangeOrderSpendEntries,
  canAddDirectCostSpendEntries,
};
